import DecreasedRiskBadge from '@/app/components/risks/common/badges/decreased-risk-badge';
import IncreasedRiskBadge from '@/app/components/risks/common/badges/increased-risk-badge';
import IconCatch from '@/components/icons/icon-catch/icon-catch';
import { RiskFactor } from '@/repositories/risk.repository';
import { formatRelativeRiskDecimal, getCancerRiskSuffix, normalizeCancerTypeName } from '@/utils';
import { Box, Flex, Text } from '@cmpsr/components';

type QuantifiableRiskFactorProps = {
  shortDescription: string;
  riskFactor: RiskFactor;
  affectedCancerTypes: string[];
  impactsByCancerType: Record<string, number>;
};

export default function QuantifiableRiskFactor(props: QuantifiableRiskFactorProps) {
  return (
    <Flex direction="column" gap="spacer-6">
      <Flex alignItems="center" gap="spacer-4">
        <IconCatch icon="risk-impact" size="l" borderRadius="10px" backgroundColor="background-page-03" />
        <Text variant="text-header-L">
          Risk impact{' '}
          <Text as="span" textStyle="text-header-l-v">
            relative to population average
          </Text>
        </Text>
      </Flex>
      <Flex
        direction="column"
        gap="spacer-6"
        backgroundColor="background-light"
        borderRadius="radii-3"
        p="spacer-6"
        border="1px solid"
        borderColor="ui-element-divider"
      >
        <Text variant="text-body-regular">
          {props.shortDescription}, which has the following impact on your cancer risk{' '}
          <Text as="span" variant="text-body-bold">
            relative to population average:
          </Text>
        </Text>
        {props.affectedCancerTypes.map((cancerType) => (
          <Flex alignItems="center" gap="spacer-4" key={cancerType}>
            <Box w="70px" textAlign="left">
              {_getBadge({
                cancerType,
                riskFactor: props.riskFactor,
                impactsByCancerType: props.impactsByCancerType,
              })}
            </Box>
            <Text variant="text-body-regular">
              {`Your ${normalizeCancerTypeName(cancerType)}${getCancerRiskSuffix(cancerType)} risk`}
            </Text>
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
}

function _getBadge(args: { cancerType: string; riskFactor: RiskFactor; impactsByCancerType: Record<string, number> }) {
  if (args.impactsByCancerType[args.cancerType] > 0) {
    return (
      <IncreasedRiskBadge
        percentage={formatRelativeRiskDecimal(args.impactsByCancerType[args.cancerType], args.cancerType)}
      />
    );
  }

  return (
    <DecreasedRiskBadge percentage={formatRelativeRiskDecimal(Math.abs(args.impactsByCancerType[args.cancerType]))} />
  );
}
